/**
 * The WebSocket connection of a player or a big screen (protocol: @handshake/protocol).
 *
 * - Opens `/ws`, sends `hello {v, kind, token}`; the server answers `welcome`, the current
 *   `view`, then the `ack`. A big screen without a token gets a new screen session: the server
 *   sends `pairingCode {code, token}`, and the connection keeps that token for reconnects
 *   (`onToken`, so the page can store it). A screen whose token the server no longer knows starts
 *   over with a new pairing code instead of ending.
 * - Reconnects with exponential backoff (with jitter) and says so (`status`), and reconnects or
 *   re-syncs at once when the tab becomes visible, regains focus or comes back online (iOS
 *   suspends background tabs).
 * - Notices dead connections that never closed (Wi-Fi lost, network switched, NAT timeout): the
 *   browser going offline drops the socket at once; after a wake, or when nothing has arrived
 *   for a while, a `ping` must be acked within a few seconds or the socket is replaced.
 * - Every message carries a unique `requestId`. Messages not acked yet are sent again, with the
 *   same id, after a reconnect; the server never applies an id twice.
 * - Views: frames whose `viewSeq` is not newer than the last one on this connection are dropped
 *   (it restarts at 1 per connection). The clock offset `serverNow - Date.now()` is measured on
 *   every view.
 */
import { CLOSE_CODES, PROTOCOL_VERSION } from "@handshake/protocol/constants";
import type { Outgoing, ServerMessage } from "./types.ts";

/** Who connects: a player with its session token, or a big screen (token once paired). */
export type Identity =
  | { readonly kind: "player"; readonly token: string }
  | { readonly kind: "screen"; readonly token: string | null };

export type ConnectionStatus = "connecting" | "open" | "reconnecting" | "ended";

export interface Ack {
  readonly ok: boolean;
  readonly error?: { readonly code: string; readonly message: string };
}

export interface ConnectionEvents {
  onStatus(status: ConnectionStatus, detail?: string): void;
  /** Every server message, in order (stale views already dropped). */
  onMessage(message: ServerMessage, receivedAt: number): void;
  /** A big screen's session token changed (new pairing session, or dropped). */
  onToken?(token: string | null): void;
}

interface Pending {
  readonly raw: string;
  readonly resolve: (ack: Ack) => void;
  sent: boolean;
}

/** The subset of the WebSocket API the connection uses (tests pass a fake). */
export interface SocketLike {
  readyState: number;
  onopen: ((ev: unknown) => void) | null;
  onclose: ((ev: { code: number; reason: string }) => void) | null;
  onmessage: ((ev: { data: unknown }) => void) | null;
  onerror: ((ev: unknown) => void) | null;
  send(data: string): void;
  close(code?: number, reason?: string): void;
}

export type SocketFactory = (url: string) => SocketLike;

let counter = 0;
export function newRequestId(): string {
  counter = (counter + 1) % 1_000_000;
  const rand = Math.random().toString(36).slice(2, 10);
  return `${Date.now().toString(36)}-${counter.toString(36)}-${rand}`;
}

function defaultUrl(): string {
  const { protocol, host } = window.location;
  return `${protocol === "https:" ? "wss:" : "ws:"}//${host}/ws`;
}

const OPEN = 1;

/** Nothing heard for this long: probe the connection with a ping. */
export const QUIET_MS = 25_000;
/** A ping (probe) not acked within this long means the connection is dead. */
export const PROBE_TIMEOUT_MS = 5_000;
const WATCH_INTERVAL_MS = 5_000;

export class GameConnection {
  private socket: SocketLike | null = null;
  private status: ConnectionStatus = "connecting";
  private attempt = 0;
  private retryTimer: ReturnType<typeof setTimeout> | null = null;
  private helloId: string | null = null;
  private authed = false;
  private lastViewSeq = 0;
  private readonly pending = new Map<string, Pending>();
  private stopped = false;
  private readonly cleanup: (() => void)[] = [];
  private token: string | null;
  /** When the current socket last received anything. */
  private lastHeard = 0;
  /** The outstanding liveness probe (a ping), if any. */
  private probe: { readonly id: string; readonly timer: ReturnType<typeof setTimeout> } | null =
    null;

  constructor(
    private readonly identity: Identity,
    private readonly events: ConnectionEvents,
    private readonly factory: SocketFactory = (url) => new WebSocket(url) as unknown as SocketLike,
    private readonly url: string = defaultUrl(),
  ) {
    this.token = identity.token;
  }

  get currentStatus(): ConnectionStatus {
    return this.status;
  }

  start(): void {
    this.open();
    if (typeof window === "undefined") return;
    const wake = () => this.wake();
    const onVisible = () => {
      if (document.visibilityState === "visible") wake();
    };
    document.addEventListener("visibilitychange", onVisible);
    // The browser knows the network is gone: the socket is dead even if it has not closed.
    const offline = () => this.drop();
    window.addEventListener("focus", wake);
    window.addEventListener("online", wake);
    window.addEventListener("offline", offline);
    window.addEventListener("pageshow", wake);
    const watch = setInterval(() => this.watch(), WATCH_INTERVAL_MS);
    this.cleanup.push(() => {
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", wake);
      window.removeEventListener("online", wake);
      window.removeEventListener("offline", offline);
      window.removeEventListener("pageshow", wake);
      clearInterval(watch);
    });
  }

  /** Every few seconds: a connection that has been quiet for a while gets a probe. */
  watch(now: number = Date.now()): void {
    if (this.authed && now - this.lastHeard >= QUIET_MS) this.checkAlive();
  }

  /** Sends a ping; if it is not acked in time, the socket is replaced. */
  private checkAlive(): void {
    const s = this.socket;
    if (!s || s.readyState !== OPEN || !this.authed || this.probe) return;
    const id = newRequestId();
    const timer = setTimeout(() => {
      if (this.probe?.id !== id) return;
      this.probe = null;
      this.drop();
    }, PROBE_TIMEOUT_MS);
    this.probe = { id, timer };
    try {
      s.send(JSON.stringify({ type: "ping", requestId: id }));
    } catch {
      this.drop();
    }
  }

  private clearProbe(): void {
    if (this.probe) clearTimeout(this.probe.timer);
    this.probe = null;
  }

  /** The socket is dead (or the network is gone): drop it and reconnect with a short backoff. */
  private drop(): void {
    if (this.stopped || this.status === "ended") return;
    this.clearProbe();
    const old = this.socket;
    this.socket = null;
    this.authed = false;
    if (old) {
      old.onclose = null;
      old.onmessage = null;
      try {
        old.close();
      } catch {
        // ignore
      }
    }
    this.attempt = 0;
    this.scheduleRetry();
  }

  stop(): void {
    this.stopped = true;
    this.clearProbe();
    for (const f of this.cleanup.splice(0)) f();
    if (this.retryTimer) clearTimeout(this.retryTimer);
    const s = this.socket;
    this.socket = null;
    if (s) {
      s.onclose = null;
      s.close(1000, "bye");
    }
    for (const p of this.pending.values()) p.resolve({ ok: false, error: STOPPED });
    this.pending.clear();
  }

  /** Sends a message; resolves with its ack (after reconnects if need be). */
  send(message: Outgoing, requestId: string = newRequestId()): Promise<Ack> {
    if (this.status === "ended" || this.stopped) {
      return Promise.resolve({ ok: false, error: STOPPED });
    }
    const existing = this.pending.get(requestId);
    if (existing) return new Promise((resolve) => this.chain(requestId, resolve));
    const raw = JSON.stringify({ ...message, requestId });
    return new Promise<Ack>((resolve) => {
      const p: Pending = { raw, resolve, sent: false };
      this.pending.set(requestId, p);
      this.flushOne(p);
    });
  }

  private chain(requestId: string, resolve: (ack: Ack) => void): void {
    const p = this.pending.get(requestId);
    if (!p) return;
    const prev = p.resolve;
    this.pending.set(requestId, {
      ...p,
      resolve: (ack) => {
        prev(ack);
        resolve(ack);
      },
    });
  }

  /** The tab woke up: reconnect now if the socket is down, otherwise ask for a fresh view. */
  wake(): void {
    if (this.stopped || this.status === "ended") return;
    if (this.socket && this.socket.readyState === OPEN && this.authed) {
      void this.send({ type: "sync" });
      // The socket may have died while the tab slept: make sure it still answers.
      this.checkAlive();
      return;
    }
    if (this.status === "reconnecting") {
      if (this.retryTimer) clearTimeout(this.retryTimer);
      this.retryTimer = null;
      this.attempt = 0;
      this.open();
    }
  }

  private setStatus(status: ConnectionStatus, detail?: string): void {
    this.status = status;
    this.events.onStatus(status, detail);
  }

  private open(): void {
    if (this.stopped) return;
    const old = this.socket;
    if (old) {
      old.onclose = null;
      old.onmessage = null;
      try {
        old.close();
      } catch {
        // ignore
      }
    }
    this.authed = false;
    this.lastViewSeq = 0;
    this.clearProbe();
    for (const p of this.pending.values()) p.sent = false;
    let socket: SocketLike;
    try {
      socket = this.factory(this.url);
    } catch {
      this.scheduleRetry();
      return;
    }
    this.socket = socket;
    socket.onopen = () => {
      this.helloId = newRequestId();
      socket.send(
        JSON.stringify({
          type: "hello",
          requestId: this.helloId,
          v: PROTOCOL_VERSION,
          kind: this.identity.kind,
          ...(this.token !== null ? { token: this.token } : {}),
        }),
      );
    };
    socket.onmessage = (ev) => {
      if (this.socket !== socket || typeof ev.data !== "string") return;
      let m: ServerMessage;
      try {
        m = JSON.parse(ev.data) as ServerMessage;
      } catch {
        return;
      }
      this.receive(m);
    };
    socket.onclose = (ev) => {
      if (this.socket !== socket) return;
      this.socket = null;
      this.authed = false;
      this.clearProbe();
      if (ev.code === CLOSE_CODES.sessionEnded) {
        this.end("You have left this room.");
        return;
      }
      // After a refused hello the status is already "ended", and this does nothing.
      this.scheduleRetry();
    };
    socket.onerror = () => {
      // onclose follows.
    };
  }

  private end(detail: string): void {
    this.setStatus("ended", detail);
    for (const p of this.pending.values()) p.resolve({ ok: false, error: STOPPED });
    this.pending.clear();
  }

  private scheduleRetry(): void {
    if (this.stopped || this.status === "ended") return;
    this.setStatus("reconnecting");
    const base = Math.min(10_000, 500 * 2 ** this.attempt);
    this.attempt++;
    const wait = base / 2 + Math.random() * (base / 2);
    if (this.retryTimer) clearTimeout(this.retryTimer);
    this.retryTimer = setTimeout(() => {
      this.retryTimer = null;
      this.open();
    }, wait);
  }

  private receive(m: ServerMessage): void {
    const now = Date.now();
    this.lastHeard = now;
    if (m.type === "ack") {
      if (this.probe && m.requestId === this.probe.id) {
        this.clearProbe();
        return;
      }
      if (m.requestId === this.helloId) {
        if (m.ok) {
          this.authed = true;
          this.attempt = 0;
          this.setStatus("open");
          for (const p of this.pending.values()) this.flushOne(p);
        } else if (m.error?.code === "unknown-session") {
          if (this.identity.kind === "screen" && this.token !== null) {
            // The screen's session is gone (e.g. the server's data was reset): pair again.
            this.token = null;
            this.events.onToken?.(null);
            this.attempt = 0;
            this.open();
            return;
          }
          this.end(m.error.message);
        }
        return;
      }
      const p = this.pending.get(m.requestId);
      if (p) {
        this.pending.delete(m.requestId);
        p.resolve({ ok: m.ok, error: m.error });
      }
      return;
    }
    if (m.type === "view") {
      if (m.viewSeq <= this.lastViewSeq) return;
      this.lastViewSeq = m.viewSeq;
    }
    if (m.type === "pairingCode" && this.identity.kind === "screen" && m.token !== this.token) {
      this.token = m.token;
      this.events.onToken?.(m.token);
    }
    this.events.onMessage(m, now);
  }

  private flushOne(p: Pending): void {
    const s = this.socket;
    if (!s || s.readyState !== OPEN || !this.authed || p.sent) return;
    p.sent = true;
    s.send(p.raw);
  }
}

const STOPPED = { code: "disconnected", message: "Not connected." };
